import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useSeoMeta } from '@unhead/react';
import { Button } from '@/components/ui/button';
import { LoginArea } from '@/components/auth/LoginArea';
import { CommentsSection } from '@/components/comments/CommentsSection';
import { ThemeToggle } from '@/components/ThemeToggle';
import { LinkPreview } from '@/components/LinkPreview';
import { TweetEmbed } from '@/components/TweetEmbed';
import { MessageSquare, ArrowLeft } from 'lucide-react';
import { useUrlHistory } from '@/hooks/useUrlHistory';
import { useState, useEffect } from 'react';

// Helper function to check if URL is a Twitter/X link
function isTwitterUrl(url: URL): boolean {
  const hostname = url.hostname.toLowerCase();
  return hostname === 'twitter.com' ||
         hostname === 'x.com' ||
         hostname === 'www.twitter.com' ||
         hostname === 'www.x.com' ||
         hostname === 'mobile.twitter.com';
}

// Helper function to parse URL from path segments
function parseUrlFromPath(pathname: string, search?: string): string | null {
  // Remove leading slash and split into segments
  const segments = pathname.slice(1).split('/');

  if (segments.length === 0 || segments[0] === '') {
    return null;
  }

  // Check if this looks like a host-based URL format
  // Format: /host.com/path/segments -> https://host.com/path/segments
  const firstSegment = segments[0];

  // If the first segment contains a dot and looks like a domain, treat it as host-based format
  // This includes bare domains like "debunkit.ai" (single segment) and domains with paths
  if (firstSegment.includes('.') && !firstSegment.includes('%')) {
    const host = firstSegment;
    const pathSegments = segments.slice(1);
    let reconstructedUrl = `https://${host}`;

    // Add path if there are additional segments
    if (pathSegments.length > 0 && pathSegments[0] !== '') {
      reconstructedUrl += `/${pathSegments.join('/')}`;
    }

    // If we have search parameters, append them
    if (search) {
      reconstructedUrl += search;
    }

    return reconstructedUrl;
  }

  // Otherwise, treat the entire path as a single encoded URL (legacy format)
  return decodeURIComponent(firstSegment);
}

const UrlComments = () => {
  const { url: encodedUrl } = useParams<{ url: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const { addToHistory } = useUrlHistory();
  const [url, setUrl] = useState<URL | null>(null);
  const [urlError, setUrlError] = useState('');

  useEffect(() => {
    try {
      let urlString: string | null = null;

      // Try to parse URL from the full pathname first (host-based format)
      urlString = parseUrlFromPath(location.pathname, location.search);

      // If that didn't work and we have a URL param, try the legacy format
      if (!urlString && encodedUrl) {
        urlString = decodeURIComponent(encodedUrl);
      }

      if (!urlString) {
        setUrlError('No URL provided');
        return;
      }

      const urlObject = new URL(urlString);
      setUrl(urlObject);
      setUrlError('');

      // Add to history when URL is successfully loaded
      addToHistory(urlObject.toString());
    } catch {
      setUrlError('Invalid URL format');
    }
  }, [encodedUrl, location.pathname, location.search, addToHistory]);

  useSeoMeta({
    title: url ? `Comments for ${url.hostname} - WebVoice` : 'WebVoice',
    description: url ? `Join the discussion about ${url.toString()} on WebVoice using the Nostr protocol.` : 'Comment on any URL with Nostr',
  });

  if (urlError) {
    return (
      <div className="min-h-screen bg-white dark:bg-gray-900">
        <header className="px-6 py-4 border-b border-gray-200 dark:border-gray-800">
          <div className="flex items-center justify-between max-w-6xl mx-auto">
            <div className="flex items-center space-x-3">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate('/')}
                className="p-2"
              >
                <ArrowLeft className="h-4 w-4" />
              </Button>
              <MessageSquare className="h-6 w-6 text-blue-600" />
              <span className="text-xl font-medium text-gray-900 dark:text-gray-100">
                WebVoice
              </span>
            </div>
            <div className="flex items-center space-x-3">
              <ThemeToggle />
              <LoginArea className="max-w-60" />
            </div>
          </div>
        </header>

        <main className="flex items-center justify-center min-h-[60vh] px-6">
          <div className="text-center space-y-6 max-w-md">
            <div className="flex items-center justify-center w-16 h-16 bg-red-50 dark:bg-red-900/20 rounded-full mx-auto">
              <MessageSquare className="h-8 w-8 text-red-500" />
            </div>
            <div>
              <h3 className="text-2xl font-light text-gray-900 dark:text-gray-100 mb-2">
                Invalid URL
              </h3>
              <p className="text-gray-600 dark:text-gray-400 mb-6">
                {urlError}
              </p>
              <Button
                onClick={() => navigate('/')}
                className="px-6 py-2 rounded-full"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Go Back Home
              </Button>
            </div>
          </div>
        </main>
      </div>
    );
  }

  if (!url) {
    return (
      <div className="min-h-screen bg-white dark:bg-gray-900">
        <div className="flex items-center justify-center min-h-screen">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white dark:bg-gray-900">
      {/* Simple Header */}
      <header className="px-6 py-4 border-b border-gray-200 dark:border-gray-800 sticky top-0 z-50 bg-white/95 dark:bg-gray-900/95 backdrop-blur-sm">
        <div className="flex items-center justify-between max-w-6xl mx-auto">
          <div className="flex items-center space-x-3">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/')}
              className="p-2"
            >
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <MessageSquare className="h-6 w-6 text-blue-600" />
            <span className="text-xl font-medium text-gray-900 dark:text-gray-100">
              WebVoice
            </span>
          </div>
          <div className="flex items-center space-x-3">
            <ThemeToggle />
            <LoginArea className="max-w-60" />
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-8">
        {/* Link Preview Header */}
        <div className="mb-8 space-y-4">
          <div className="flex items-center justify-between">
            <h1 className="text-2xl font-semibold text-gray-900 dark:text-gray-100">
              Discussion
            </h1>
          </div>

          {/* Render TweetEmbed for Twitter/X links, LinkPreview for others */}
          {isTwitterUrl(url) ? (
            <TweetEmbed url={url} />
          ) : (
            <LinkPreview url={url} />
          )}
        </div>

        {/* Comments Section */}
        <CommentsSection
          root={url}
          title="Comments"
          emptyStateMessage="No comments yet for this URL"
          emptyStateSubtitle="Be the first to start the conversation!"
          className="-mx-6 px-6 sm:px-0 sm:mx-0"
        />
      </main>

      {/* Simple Footer */}
      <footer className="text-center py-8 text-sm text-gray-500 dark:text-gray-400 space-x-6 border-t border-gray-200 dark:border-gray-800 mt-16">
        <a
          href="https://nostr.com"
          target="_blank"
          rel="noopener noreferrer"
          className="hover:text-gray-700 dark:hover:text-gray-300"
        >
          Powered by Nostr
        </a>
        <a
          href="https://soapbox.pub/mkstack"
          target="_blank"
          rel="noopener noreferrer"
          className="hover:text-gray-700 dark:hover:text-gray-300"
        >
          Vibed with MKStack
        </a>
      </footer>
    </div>
  );
};

export default UrlComments;